import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { LineStyle } from "building-types/views/Primitive2DOutput";
import { sheetPolylineGeometryNew } from "building-types/views/sheetGeometryTypes";
import { ONE_POINT } from "common/constants/scaleFactors";
import type { SheetInches } from "common/types/nominalNumbersTypes";
import { numMul, numSub } from "common/utils/nominalNumbers";
import { inches } from "doc/pdf/drawingLayout";
import { MARGIN_INCHES, MARGIN_LEFT_INCHES, PAGE_HEIGHT_INCHES, PAGE_WIDTH_INCHES } from "doc/plans/constants";
import { polyLine2ByTwoPoints } from "geom/polyLine2";
import { rect2ByBottomLeftWidthHeight } from "geom/rect2";
import { makeGeometrySheetNode } from "./nodes/geometrySheetNode";
import type { GeometrySheetNode, Page } from "./sheetNode";

export function makeRectangleSheetEntity({
  x,
  y,
  width,
  height,
  linewidth = ONE_POINT,
  handle,
  ...style
}: {
  x: SheetInches;
  y: SheetInches;
  width: SheetInches;
  height: SheetInches;
  handle?: DocEntityHandle;
} & LineStyle): GeometrySheetNode {
  return makeGeometrySheetNode(
    sheetPolylineGeometryNew({
      paths: [rect2ByBottomLeftWidthHeight(x, y, width, height)],
      linewidth,
      handle,
      ...style,
    }),
  );
}

export function makeHorizontalDividerSheetEntity({
  x,
  y,
  width,
  linewidth = ONE_POINT,
  ...style
}: {
  x: number;
  y: number;
  width: number;
} & LineStyle): GeometrySheetNode {
  return makeGeometrySheetNode(
    sheetPolylineGeometryNew({
      paths: [polyLine2ByTwoPoints(x, y, x + width, y)],
      linewidth,
      ...style,
    }),
  );
}

export function makeVerticalDividerSheetEntity({
  x,
  y,
  height,
  linewidth = ONE_POINT,
  ...style
}: {
  x: number;
  y: number;
  height: number;
} & LineStyle): GeometrySheetNode {
  return makeGeometrySheetNode(
    sheetPolylineGeometryNew({
      paths: [polyLine2ByTwoPoints(x, y, x, y + height)],
      linewidth: ONE_POINT,
      ...style,
    }),
  );
}

export function makePageMarginSheetEntity() {
  return makeRectangleSheetEntity({
    x: inches(0),
    y: inches(0),
    width: numSub(numSub(PAGE_WIDTH_INCHES, MARGIN_LEFT_INCHES), MARGIN_INCHES),
    height: numSub(PAGE_HEIGHT_INCHES, numMul(MARGIN_INCHES, 2)),
  });
}

export function getPageMargin(marginOrPage: Page | number | undefined): number {
  const defaultValue = 36;
  if (marginOrPage === undefined) {
    return defaultValue;
  }
  if (typeof marginOrPage === "number") {
    return marginOrPage;
  }
  return marginOrPage.margin ?? defaultValue;
}
